/**
 * Mock data for local development
 *
 * Provides sample documents, explorer tags, and upload responses
 * so the frontend can run locally without hitting the production API.
 */

// ---------------------------------------------------------------------------
// Sample documents (full shape matching GET /api/doc/:ref)
// ---------------------------------------------------------------------------

const DOCS = {
  'api-feb17rhn': {
    doc_reference: 'api-feb17rhn',
    title: 'Application Core & Web Server Specification',
    jsond_content: {
      jsond: '1.0',
      title: 'Application Core & Web Server Specification',
      root: [
        {
          id: '1',
          level: 1,
          heading: 'Overview',
          body: [
            'This document describes the core application architecture for the email-loop-api service, a Node.js Express application that serves as the backend for the JSOND document management platform.',
            'The service handles document ingestion via email webhooks, batch ZIP uploads, and provides REST APIs for document retrieval and exploration.'
          ],
          children: [
            {
              id: '1.1',
              level: 2,
              heading: 'Technology Stack',
              body: ['Built on Node.js with Express 4.x as the HTTP framework. Uses Winston for structured logging, Multer for multipart file handling, and CORS middleware for cross-origin requests.'],
              children: []
            },
            {
              id: '1.2',
              level: 2,
              heading: 'Entry Point',
              body: ['The application entry point is app.js which configures middleware, mounts route handlers, and starts the HTTP server on the configured port (default 8080 for Cloud Run).'],
              children: []
            }
          ]
        },
        {
          id: '2',
          level: 1,
          heading: 'Route Architecture',
          body: [
            'Routes are organized by functional area: authentication, document retrieval, explorer browsing, email webhook ingestion, and batch upload processing.'
          ],
          children: [
            {
              id: '2.1',
              level: 2,
              heading: 'Authentication Routes',
              body: ['Firebase ID tokens are validated on protected routes. The /api/auth/login endpoint exchanges a Firebase token for user and tenant context.'],
              children: []
            },
            {
              id: '2.2',
              level: 2,
              heading: 'Document Routes',
              body: ['GET /api/doc/:reference returns full document content including JSOND, metadata, iteration history, and research sources. Attachments are streamed from Cloud Storage.'],
              children: []
            }
          ]
        },
        {
          id: '3',
          level: 1,
          heading: 'Error Handling',
          body: [
            'All route handlers are wrapped in asyncHandler which catches promise rejections and forwards them to the Express error handler. Errors are logged with request context via Winston.'
          ],
          children: []
        }
      ]
    },
    metadata: {
      tags: {
        technology: ['node-js', 'express', 'winston'],
        context: ['backend-development', 'api-development', 'web-server-configuration', 'error-handling'],
        project: ['document-management']
      },
      doc_type: 'specification'
    },
    iteration_count: 2,
    created_at: '2026-02-15T14:30:00Z',
    last_updated: '2026-02-16T09:15:00Z',
    iterations: [
      { iteration_number: 1, changes_made: 'Initial document generation from source code analysis', missing_context: null, created_at: '2026-02-15T14:30:00Z' },
      { iteration_number: 2, changes_made: 'Added error handling patterns and middleware configuration details', missing_context: null, created_at: '2026-02-16T09:15:00Z' }
    ],
    research_sources: [
      { url: 'https://expressjs.com/en/guide/error-handling.html', title: 'Express Error Handling Guide', domain: 'expressjs.com', fetched_at: '2026-02-15T14:32:00Z' },
      { url: 'https://cloud.google.com/run/docs/quickstarts', title: 'Cloud Run Quickstarts', domain: 'cloud.google.com', fetched_at: '2026-02-15T14:33:00Z' },
      { url: 'https://github.com/winstonjs/winston', title: 'Winston Logger', domain: 'github.com', fetched_at: '2026-02-16T09:16:00Z' }
    ]
  },

  'ai-feb17xnp': {
    doc_reference: 'ai-feb17xnp',
    title: 'AI Analysis Service Overview',
    jsond_content: {
      jsond: '1.0',
      title: 'AI Analysis Service Overview',
      root: [
        {
          id: '1',
          level: 1,
          heading: 'AI Pipeline Architecture',
          body: [
            'The AI analysis service uses Google Vertex AI with Gemini 2.5 Flash to process documents through a multi-pass pipeline. Each pass focuses on a specific aspect of document understanding.',
            'The pipeline extracts facts, structures content into JSOND format, cross-references related documents, and normalizes tags for consistency.'
          ],
          children: [
            {
              id: '1.1',
              level: 2,
              heading: 'Extraction Phase',
              body: ['The extractFacts() function analyzes raw content and produces structured metadata including title, summary, tags, and key concepts. It uses the unified extraction prompt template.'],
              children: []
            },
            {
              id: '1.2',
              level: 2,
              heading: 'Structure Phase',
              body: ['The structureDocument() function takes extracted facts and organizes them into a hierarchical JSOND tree with sections, subsections, and body paragraphs.'],
              children: []
            }
          ]
        },
        {
          id: '2',
          level: 1,
          heading: 'Model Configuration',
          body: [
            'Three model tiers are available: Gemini 2.5 Pro for high-stakes classification, Gemini 2.5 Flash for general processing, and Gemini 2.5 Flash Lite for lightweight tasks like tag deduplication.'
          ],
          children: []
        }
      ]
    },
    metadata: {
      tags: {
        technology: ['vertex-ai', 'postgres', 'node-js'],
        context: ['ai', 'document-processing', 'api-development', 'implementation'],
        project: ['document-management']
      },
      doc_type: 'specification'
    },
    iteration_count: 1,
    created_at: '2026-02-16T09:20:00Z',
    last_updated: '2026-02-16T09:20:00Z',
    iterations: [
      { iteration_number: 1, changes_made: 'Initial generation from AI service source code', missing_context: null, created_at: '2026-02-16T09:20:00Z' }
    ],
    research_sources: [
      { url: 'https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/gemini', title: 'Gemini API Reference', domain: 'cloud.google.com', fetched_at: '2026-02-16T09:21:00Z' },
      { url: 'https://ai.google.dev/gemini-api/docs/structured-output', title: 'Structured Output with Gemini', domain: 'ai.google.dev', fetched_at: '2026-02-16T09:22:00Z' }
    ]
  },

  'db-feb17hge': {
    doc_reference: 'db-feb17hge',
    title: 'Database & Data Modeling',
    jsond_content: {
      jsond: '1.0',
      title: 'Database & Data Modeling',
      root: [
        {
          id: '1',
          level: 1,
          heading: 'Data Model',
          body: [
            'The application uses PostgreSQL 15 via Cloud SQL with a database named "docman". All tables use UUID primary keys and support JSONB metadata columns for flexible schema evolution.'
          ],
          children: [
            {
              id: '1.1',
              level: 2,
              heading: 'Core Tables',
              body: [
                'Documents table: stores JSOND content, metadata (tags, doc_type), state, and timestamps.',
                'Users and tenant_members tables: multi-tenant isolation with email-based user lookup.',
                'Document iterations: version history with change descriptions.'
              ],
              children: []
            },
            {
              id: '1.2',
              level: 2,
              heading: 'Tag Storage',
              body: ['Tags are stored as nested JSONB objects within the metadata column: metadata.tags.technology, metadata.tags.context, metadata.tags.project. This allows flexible querying via PostgreSQL JSONB operators.'],
              children: []
            }
          ]
        },
        {
          id: '2',
          level: 1,
          heading: 'Cloud Storage Integration',
          body: [
            'File attachments (PDFs, images) are stored in Google Cloud Storage bucket gs://docman-docs. The documents table references attachments via metadata.attachments JSONB array with gcsPath, filename, contentType, and size fields.'
          ],
          children: []
        }
      ]
    },
    metadata: {
      tags: {
        technology: ['postgres', 'google-cloud-storage'],
        context: ['database', 'data-management', 'ai', 'backend-development'],
        project: ['document-management']
      },
      doc_type: 'specification'
    },
    iteration_count: 1,
    created_at: '2026-02-14T16:00:00Z',
    last_updated: '2026-02-14T16:00:00Z',
    iterations: [
      { iteration_number: 1, changes_made: 'Generated from database schema and repository layer analysis', missing_context: null, created_at: '2026-02-14T16:00:00Z' }
    ],
    research_sources: [
      { url: 'https://www.postgresql.org/docs/15/datatype-json.html', title: 'PostgreSQL JSON Types', domain: 'postgresql.org', fetched_at: '2026-02-14T16:01:00Z' },
      { url: 'https://cloud.google.com/sql/docs/postgres', title: 'Cloud SQL for PostgreSQL', domain: 'cloud.google.com', fetched_at: '2026-02-14T16:02:00Z' },
      { url: 'https://cloud.google.com/storage/docs/introduction', title: 'Cloud Storage Overview', domain: 'cloud.google.com', fetched_at: '2026-02-14T16:03:00Z' }
    ]
  }
};

// ---------------------------------------------------------------------------
// Explorer tags response (matching GET /api/explorer/tags)
// ---------------------------------------------------------------------------

export const MOCK_TAGS = {
  tags: {
    technology: [
      { name: 'node-js', count: 2 },
      { name: 'express', count: 1 },
      { name: 'postgres', count: 2 },
      { name: 'vertex-ai', count: 1 },
      { name: 'winston', count: 1 },
      { name: 'google-cloud-storage', count: 1 }
    ],
    context: [
      { name: 'backend-development', count: 2 },
      { name: 'api-development', count: 2 },
      { name: 'ai', count: 2 },
      { name: 'document-processing', count: 1 },
      { name: 'database', count: 1 },
      { name: 'data-management', count: 1 },
      { name: 'web-server-configuration', count: 1 },
      { name: 'error-handling', count: 1 },
      { name: 'implementation', count: 1 }
    ],
    project: [
      { name: 'document-management', count: 3, latest_updated: '2026-02-16T09:20:00Z' },
      { name: 'onboarding', count: 2, latest_updated: '2026-02-20T11:00:00Z' },
      { name: 'infrastructure', count: 1, latest_updated: '2026-02-18T08:30:00Z' }
    ]
  },
  untagged: {
    technology: 0,
    context: 0,
    project: 2
  },
  totalDocuments: 8
};

const EXTRA_DOCS = [
  {
    doc_reference: 'onb-feb20aaa',
    title: 'New Hire Checklist',
    last_updated: '2026-02-20T11:00:00Z',
    metadata: { tags: { technology: [], context: ['onboarding'], project: ['onboarding'] }, doc_type: 'guide' }
  },
  {
    doc_reference: 'onb-feb20bbb',
    title: 'Team Structure & Responsibilities',
    last_updated: '2026-02-19T15:30:00Z',
    metadata: { tags: { technology: [], context: ['onboarding'], project: ['onboarding'] }, doc_type: 'reference' }
  },
  {
    doc_reference: 'inf-feb18ccc',
    title: 'Cloud Run Deployment Runbook',
    last_updated: '2026-02-18T08:30:00Z',
    metadata: { tags: { technology: ['node-js'], context: ['backend-development'], project: ['infrastructure'] }, doc_type: 'runbook' }
  },
  {
    doc_reference: 'unt-feb21ddd',
    title: 'Meeting Notes - Q1 Planning',
    last_updated: '2026-02-21T14:00:00Z',
    metadata: { tags: { technology: [], context: [], project: [] }, doc_type: 'notes' }
  },
  {
    doc_reference: 'unt-feb22eee',
    title: 'Competitive Landscape Draft',
    last_updated: '2026-02-22T10:45:00Z',
    metadata: { tags: { technology: [], context: [], project: [] }, doc_type: 'research' }
  }
];

// ---------------------------------------------------------------------------
// Explorer documents (matching GET /api/explorer/documents)
// ---------------------------------------------------------------------------

const ALL_DOCS_LIST = [
  ...Object.values(DOCS).map(d => ({
    doc_reference: d.doc_reference,
    title: d.title,
    last_updated: d.last_updated,
    metadata: d.metadata
  })),
  ...EXTRA_DOCS
];

export function getMockDocuments(params = {}) {
  let results = [...ALL_DOCS_LIST];

  if (params.category && params.tag) {
    if (params.tag === 'Unassigned') {
      results = results.filter(d => {
        const catTags = d.metadata?.tags?.[params.category] || [];
        return catTags.length === 0;
      });
    } else {
      results = results.filter(d => {
        const catTags = d.metadata?.tags?.[params.category] || [];
        return catTags.includes(params.tag);
      });
    }
  }

  if (params.search) {
    const q = params.search.toLowerCase();
    results = results.filter(d =>
      d.title.toLowerCase().includes(q) ||
      d.doc_reference.toLowerCase().includes(q)
    );
  }

  // Return project_intel when filtering by a specific project tag
  let project_intel = null;
  if (params.category === 'project' && params.tag && params.tag !== 'Unassigned') {
    project_intel = {
      name: params.tag,
      intel: 'This topic covers all documentation related to ' + params.tag.replace(/-/g, ' ') + '. It includes specifications, guides, and reference materials maintained by the team.',
      created_at: '2026-02-15T10:00:00Z'
    };
  }

  return { documents: results, project_intel };
}

// ---------------------------------------------------------------------------
// Single document (matching GET /api/doc/:ref)
// ---------------------------------------------------------------------------

export function getMockDocument(reference) {
  return DOCS[reference] || null;
}

// ---------------------------------------------------------------------------
// Mock query response (matching POST /api/query)
// ---------------------------------------------------------------------------

export const MOCK_QUERY_RESPONSE = {
  answer: "Based on the documentation, the application uses a Node.js Express backend deployed on Cloud Run. The core architecture includes:\n\n1. Express 4.x as the HTTP framework with route handlers organized by functional area\n2. PostgreSQL 15 via Cloud SQL for document storage with JSONB metadata and pgvector embeddings\n3. Google Vertex AI with Gemini 2.5 Flash for AI-powered document processing\n4. Cloud Storage (gs://docman-docs) for file attachments\n\nDocuments are ingested via email webhooks or batch ZIP uploads, then processed through a multi-pass AI pipeline that extracts facts, structures content into JSOND format, and normalizes tags.",
  sources: [
    { title: 'Application Core & Web Server Specification', doc_reference: 'api-feb17rhn', similarity: 0.94, sections: ['Overview', 'Route Architecture'] },
    { title: 'AI Analysis Service Overview', doc_reference: 'ai-feb17xnp', similarity: 0.87, sections: ['AI Pipeline Architecture'] },
    { title: 'Database & Data Modeling', doc_reference: 'db-feb17hge', similarity: 0.82, sections: ['Data Model', 'Cloud Storage Integration'] }
  ],
  timing_ms: 1240
};

// ---------------------------------------------------------------------------
// Mock ideation items (matching GET /api/ideation)
// ---------------------------------------------------------------------------

export const MOCK_IDEATION_ITEMS = [
  {
    id: 'idea-001',
    title: 'Add dark mode to the document viewer',
    idea: 'The document viewer should support a dark theme toggle. Many developers prefer dark mode for extended reading sessions, and it would reduce eye strain when reviewing documentation at night.',
    status: 'published',
    origin: 'manual',
    created_at: '2026-03-20T10:00:00Z'
  },
  {
    id: 'idea-002',
    title: 'Slack integration for document notifications',
    idea: 'When a new document is absorbed or updated, post a summary to a configured Slack channel. This keeps the team aware of knowledge base changes without checking the platform.',
    status: 'published',
    origin: 'manual',
    created_at: '2026-03-19T15:30:00Z'
  },
  {
    id: 'idea-003',
    title: 'Auto-link related documents using embeddings',
    idea: 'Use vector similarity to automatically suggest related documents when viewing a doc. Surface a "Related" section at the bottom of each document with the top 3-5 most similar docs.',
    status: 'draft',
    origin: 'manual',
    created_at: '2026-03-18T09:45:00Z'
  },
  {
    id: 'idea-004',
    title: 'Document expiry warnings',
    idea: 'Flag documents that haven\'t been updated in over 6 months with a staleness indicator. Allow project leads to mark docs as "evergreen" to exempt them from staleness checks.',
    status: 'draft',
    origin: 'manual',
    created_at: '2026-03-17T12:00:00Z'
  }
];

export function mockCreateIdeation(title, idea, status = 'published') {
  const item = {
    id: 'idea-' + Date.now(),
    title,
    idea,
    status,
    origin: 'manual',
    created_at: new Date().toISOString()
  };
  MOCK_IDEATION_ITEMS.unshift(item);
  return new Promise((resolve) => {
    setTimeout(() => resolve(item), 300);
  });
}

// ---------------------------------------------------------------------------
// Mock findings (matching GET /api/explorer/findings)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Mock hypotheses (matching GET /api/discovery/hypotheses)
// ---------------------------------------------------------------------------

export const MOCK_HYPOTHESES = [
  {
    id: 'hyp-001',
    reasoning: "Document 'corporate-mar23u5y' states Ada's total funding is approximately $200 million, whereas 'market-mar23xxb' reports it as $1.3 billion. This is a material contradiction. The $1.3 billion figure likely represents a data entry error, potentially confusing total funding with the company's $1.2 billion valuation or adding an extra zero to the $130 million funding round mentioned in other facts. It is not a temporal update as both documents are from the same period.",
    type: 'contradiction',
    category: 'risk',
    title: 'Material discrepancy in Ada total funding figures',
    evidence_facts: ['8f3b3665', '45b8943f'],
    severity: 'high',
    recommended_action: "Reconcile Ada's total funding data; verify if the $1.3B figure in the market document is a typo for valuation or a specific funding round.",
    context: {
      doc_references: ['corporate-mar23u5y', 'market-mar23xxb'],
      doc_titles: ['Corporate Profile and Strategy', 'Market Dynamics and Risk'],
      topic_ids: [],
      tags: ['corporate-strategy', 'financials', 'ada-cx', 'competitive-analysis']
    },
    created_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: 'hyp-002',
    reasoning: "There is a significant numerical conflict regarding Yellow.ai's workforce. One fact states a headcount of 889, while another claims a layoff of 'over 100 employees' represents 'approx 30% of the workforce' (which would imply a total workforce of only ~333). Furthermore, other facts cite the layoff count as only 40-50 employees. These values are mathematically incompatible and do not align with a single consistent organizational state, suggesting unreliable reporting on workforce stability.",
    type: 'contradiction',
    category: 'risk',
    title: 'Conflicting data on Yellow.ai headcount and layoff scale',
    evidence_facts: ['817da91f', '6f3ab0f6', 'b56836fe'],
    severity: 'medium',
    recommended_action: "Clarify Yellow.ai's actual headcount and the true impact of their layoffs to accurately assess their remaining engineering and support capacity.",
    context: {
      doc_references: ['operational-mar23gyk', 'platform-mar23wqn'],
      doc_titles: ['Operational Health and Sentiment', 'Platform Technology Architecture'],
      topic_ids: [],
      tags: ['operations', 'risk-assessment', 'yellow-ai', 'enterprise-ai']
    },
    created_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: 'hyp-003',
    reasoning: "Multiple documents independently report signals of financial and operational distress for Yellow.ai: a 30% workforce reduction, a total freeze on salary increases and appraisals for two years, and a lack of new venture funding since August 2021. While revenue growth is reported, these convergent signals suggest a company under significant pressure to reach profitability or manage cash burn. This is a consistent pattern across four separate documents.",
    type: 'cross_document_pattern',
    category: 'opportunity',
    title: 'Convergent distress signals for Yellow.ai',
    evidence_facts: ['7e5f73f9', 'c49487e8', 'a69e8b24', '4b050893'],
    severity: 'high',
    recommended_action: "Target Yellow.ai's enterprise customer base with marketing and sales messaging focused on platform stability and long-term financial viability.",
    context: {
      doc_references: ['market-mar23j5d', 'operational-mar23gyk', 'market-mar23nt3'],
      doc_titles: ['Market Strategy & Intelligence', 'Operational Health and Sentiment', 'Market and Competitive Strategy'],
      topic_ids: [],
      tags: ['sales-strategy', 'competitive-intelligence', 'financials', 'yellow-ai', 'agentic-ai']
    },
    created_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: 'hyp-004',
    reasoning: "Ada CX shows a pattern of operational instability across multiple documents, including a low Trustpilot rating (2.0/5.0), the departure of its CTO (Jessica Popp), and multiple rounds of layoffs (both disclosed 16% and undisclosed additional rounds). These are not isolated incidents but a recurring theme of reputational and leadership risk that contrasts with its high valuation. This pattern suggests a potential opening for competitors to capture dissatisfied clients.",
    type: 'cross_document_pattern',
    category: 'opportunity',
    title: 'Ada CX operational and reputational decline',
    evidence_facts: ['938a548f', '6663d61c', 'b31abf8a', 'df99b234'],
    severity: 'high',
    recommended_action: "Monitor Ada's enterprise account sentiment and leadership transitions to identify displacement opportunities in the CX market.",
    context: {
      doc_references: ['market-mar23xxb'],
      doc_titles: ['Market Dynamics and Risk'],
      topic_ids: [],
      tags: ['risk-assessment', 'sales-positioning', 'ada-cx', 'competitive-analysis']
    },
    created_at: new Date(Date.now() - 3600000).toISOString()
  },
  {
    id: 'hyp-005',
    reasoning: "A clear strategic shift is visible across all major entities (Kore.ai, Yellow.ai, and Ada), moving from traditional 'Conversational AI' to 'Agentic AI' or 'Autonomous Agents.' This is evidenced by Yellow.ai's 'Nexus' launch, Ada's 'Agentic AI' differentiator, and Kore.ai's 'Agentic RAG' technology. This is not a coincidence but a fundamental market transition. This is a market-wide shift rather than a single-company update, indicating a new baseline for enterprise requirements.",
    type: 'cross_document_pattern',
    category: 'opportunity',
    title: 'Unified competitive pivot toward Agentic AI architectures',
    evidence_facts: ['1f23ff1b', '01caacab', '8f70392f', 'efd2941c'],
    severity: 'medium',
    recommended_action: "Ensure the internal product roadmap prioritizes autonomous agent capabilities to maintain competitive parity.",
    context: {
      doc_references: ['operational-mar23gyk', 'market-mar23xxb', 'platform-mar23frw', 'market-mar23nt3'],
      doc_titles: ['Operational Health and Sentiment', 'Market Dynamics and Risk', 'Platform Architecture & Governance', 'Market and Competitive Strategy'],
      topic_ids: [],
      tags: ['operations', 'risk-assessment', 'yellow-ai', 'ada-cx', 'kore-ai', 'agentic-ai', 'market-analysis', 'competition']
    },
    created_at: new Date(Date.now() - 3600000).toISOString()
  }
];

